import { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { PlusOutlined } from '@ant-design/icons';
import {
  AdditionalFileModal,
  DataTable,
  GroupHealthInsurancePoliciesModal,
  AuthorizedButton,
  PageTitle,
  Container,
  SRangePicker,
  SExportButton,
} from '../../../../components';
import { tableActions } from '../../../../store/reducers/tableReducer';
import styles from './index.module.scss';
import { INITIAL_VALUES } from './const';
import { useAppDispatch, useAppSelector } from '../../../../store';
import useHttpService from '../../../../services/hook';
import { getColumns } from './columns';
import validationSchema from './validations';
import { IExpandedRow } from '../../../../components/DataTable/type';
import { IEnum } from '../../../../services/type';
import {
  IFile,
  IGroupHealthInsurancePoliciesDataSource,
  IGroupHealthInsurancePoliciesInitialValues,
} from '../../../../types';
import { Checkbox, Flex, Typography } from 'antd';
import { modalTypes } from '../../../../assets/enums';
import globalStyles from '../../../../assets/styles/index.module.scss';
import { downloadFile } from '../../../../helpers/File';
import {
  cleanupAfterSubmit,
  prepareModalData,
} from '../../../../utils/functions';
import useFetchParameters from '../../../../hooks';
import GroupHealthInsurancePoliciesService from '../../../../api-clients/GroupHealthInsurancePoliciesService';
import EnumService from '../../../../api-clients/EnumService';

const Policies: FC = () => {
  const search = useAppSelector((state) => state.tableReducer.search);
  const sort = useAppSelector((state) => state.tableReducer.sort);
  const page = useAppSelector((state) => state.tableReducer.page);
  const dispatch = useAppDispatch();
  const executeService = useHttpService();
  const organizations = useAppSelector(
    (state) => state.organizationReducer.organizations,
  );

  // State
  const [selectedItem, setSelectedItem] = useState<
    IGroupHealthInsurancePoliciesDataSource | undefined
  >(undefined);
  const [additionalFile, setAdditionalFile] = useState<IFile[] | undefined>(
    undefined,
  );
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [dataSource, setDataSource] = useState<
    IGroupHealthInsurancePoliciesDataSource[]
  >([]);
  const [isExpired, setIsExpired] = useState<boolean | undefined>(undefined);
  const [dates, setDates] = useState<string[]>([]);
  const [expandedRows, setExpandedRows] = useState<IExpandedRow[]>([]);
  const [statusChoices, setStatusChoices] = useState<IEnum[]>([]);
  const [branch, setBranch] = useState<IEnum[]>([]);
  const [policyTypes, setPolicyTypes] = useState<IEnum[]>([]);
  const [additionalFileID, setAdditionalFileID] = useState<number | undefined>(
    undefined,
  );
  const [tempAdditionalFiles, setTempAdditionalFiles] = useState<
    FileList | undefined
  >(undefined);

  const parameters = useFetchParameters({
    search,
    sort,
    page,
    dates,
    isExpired,
  });
  const groupHealthInsurancePoliciesService = useMemo(
    () => new GroupHealthInsurancePoliciesService(executeService, parameters),
    [executeService, parameters],
  );
  const enumService = useMemo(
    () => new EnumService(executeService),
    [executeService],
  );

  // Handlers
  const handleFetchEnums = async () => {
    const statusResponse =
      await enumService.fetchGroupHealthInsurancePoliciesStatusChoices();
    const branchResponse =
      await enumService.fetchGroupHealthInsurancePolicyTypes();
    const policyResponse = await enumService.fetchDomesticInsuranceTypes();
    if (statusResponse && Array.isArray(statusResponse)) {
      setStatusChoices(statusResponse);
    }
    if (branchResponse && Array.isArray(branchResponse)) {
      setBranch(branchResponse);
    }
    if (policyResponse && Array.isArray(policyResponse)) {
      setPolicyTypes(policyResponse);
    }
  };

  const handleFetchData = async () => {
    const response = await groupHealthInsurancePoliciesService.fetchData();
    if (response && 'results' in response) {
      dispatch(tableActions.changeTableTotal(response.count));
      const results = response.results.reduce<
        IGroupHealthInsurancePoliciesDataSource[]
      >(
        (
          prev: IGroupHealthInsurancePoliciesDataSource[],
          cur: IGroupHealthInsurancePoliciesDataSource,
        ) => {
          return [...prev, { ...cur, key: cur.id }];
        },
        [],
      );
      setDataSource(results);
      if (additionalFileID) {
        const item = results.find((item) => item.id === additionalFileID);
        if (item) {
          setAdditionalFileID(item.id);
          setAdditionalFile(item.additional_files);
        }
      }
    }
  };

  const handleClickEye = async (id: number) => {
    dispatch(tableActions.changeTableRow(id));
    const rows =
      await groupHealthInsurancePoliciesService.fetchExpandedRows(id);
    if (rows && Array.isArray(rows)) {
      setExpandedRows(rows);
    }
  };

  const handleChangeExpired = () => setIsExpired((prev) => !prev);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    formik.setErrors({});
    formik.setTouched({});
  };

  const handleOpenEditModal = async (id: number) => {
    const item = await groupHealthInsurancePoliciesService.fetchItemById(id);
    if (item && 'id' in item) {
      setSelectedItem(item);
    }
  };

  const handleCloseEditModal = () => {
    setSelectedItem(undefined);
    formik.setErrors({});
    formik.setTouched({});
  };

  const handleUploadFile = (e: ChangeEvent<HTMLInputElement>) =>
    formik.setFieldValue('upload_files', e.target.files);

  const handleClickCancel = async (id: number) => {
    await groupHealthInsurancePoliciesService.cancelPolicy(id);
    cleanupAfterSubmit<IGroupHealthInsurancePoliciesDataSource>(
      setIsModalOpen,
      setSelectedItem,
      formik.resetForm,
      handleFetchData,
    );
  };

  const handleClickDelete = async (id: number) => {
    await groupHealthInsurancePoliciesService.deleteItemById(id);
    cleanupAfterSubmit<IGroupHealthInsurancePoliciesDataSource>(
      setIsModalOpen,
      setSelectedItem,
      formik.resetForm,
      handleFetchData,
    );
  };

  const handleClickExport = async () => {
    const response = await groupHealthInsurancePoliciesService.exportData();
    if (response && 'download_url' in response) {
      downloadFile(response.download_url);
    }
  };

  const handleSubmit = async (
    values: IGroupHealthInsurancePoliciesInitialValues,
  ) => {
    try {
      await groupHealthInsurancePoliciesService.submitForm(
        values,
        selectedItem,
      );

      if (selectedItem) {
        await groupHealthInsurancePoliciesService.updateStatus(
          selectedItem.id,
          values.recent_status,
          values.email_users || [],
        );
      }
    } catch (e) {
      console.log(e);
    } finally {
      cleanupAfterSubmit<IGroupHealthInsurancePoliciesDataSource>(
        setIsModalOpen,
        setSelectedItem,
        formik.resetForm,
        handleFetchData,
      );
      dispatch(tableActions.changeTablePage({ number: 0, size: 20 }));
    }
  };

  const handleSetAdditionalFile = (id: number, file: IFile[]) => {
    setAdditionalFileID(id);
    setAdditionalFile(file);
  };

  const handleUploadAdditionalFile = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setTempAdditionalFiles(e.target.files);
    }
  };

  const handleCloseAdditionalFile = () => {
    setAdditionalFileID(undefined);
    setAdditionalFile(undefined);
    setTempAdditionalFiles(undefined);
  };

  const handleDeleteFile = async (id: number, fileId: number) => {
    await groupHealthInsurancePoliciesService.deleteFile(id, fileId);
    handleFetchData();
  };

  const handleSubmitFile = async () => {
    if (additionalFileID && tempAdditionalFiles) {
      await groupHealthInsurancePoliciesService.uploadFiles(
        additionalFileID,
        tempAdditionalFiles,
      );
      setTempAdditionalFiles(undefined);
      handleFetchData();
    }
  };

  // Columns
  const columns = getColumns(
    handleClickEye,
    handleOpenEditModal,
    handleClickDelete,
    handleClickCancel,
    handleSetAdditionalFile,
    organizations,
    statusChoices,
    policyTypes,
    branch,
  );

  // Form Management
  const formik = useFormik<IGroupHealthInsurancePoliciesInitialValues>({
    initialValues: INITIAL_VALUES,
    validationSchema,
    onSubmit: handleSubmit,
  });

  // Effects
  useEffect(() => {
    handleFetchEnums();
  }, []);

  useEffect(() => {
    if (selectedItem) {
      const result = prepareModalData<
        IGroupHealthInsurancePoliciesDataSource,
        IGroupHealthInsurancePoliciesInitialValues
      >(selectedItem);
      result && formik.setValues(result);
    } else {
      formik.setValues(INITIAL_VALUES);
    }
  }, [selectedItem]);

  useEffect(() => {
    handleFetchData();
  }, [isExpired, dates, search, sort.key, sort.order, page.number, page.size]);

  // JSX Return
  return (
    <Container>
      <PageTitle title="Grup Sağlık Sigortası - Poliçeler" />
      <Flex vertical gap="middle">
        <Flex justify="space-between">
          <Flex vertical gap="small">
            <SRangePicker
              placeholder={['Başlangıç Tarihi', 'Bitiş Tarihi']}
              onChange={(_, dates: [string, string]) => setDates(dates)}
            />
            <Flex gap="small">
              <Checkbox checked={isExpired} onChange={handleChangeExpired} />
              <Typography className={globalStyles.outOfDateText}>
                Sadece Aktif Poliçeleri Göster
              </Typography>
            </Flex>
          </Flex>
          <Flex gap={5}>
            <SExportButton onClick={handleClickExport} />
            <AuthorizedButton
              model="grouphealthinsurancepolicies"
              action="add"
              className={styles.button}
              onClick={handleOpenModal}
            >
              <PlusOutlined className={styles.buttonIcon} />
              <div className={styles.buttonText}>Yeni Poliçe Talebi</div>
            </AuthorizedButton>
          </Flex>
        </Flex>
        <DataTable<IGroupHealthInsurancePoliciesDataSource>
          scrollable
          columns={columns}
          dataSource={dataSource}
          expandedRows={expandedRows}
          statusChoices={statusChoices}
        />
      </Flex>
      <GroupHealthInsurancePoliciesModal
        type={modalTypes.CREATE}
        open={isModalOpen}
        title="Yeni Grup Sağlık Poliçe Talebi Oluşturma"
        subTitle="Grup Sağlık Sigortası İçin Yeni Poliçe Oluşturma"
        formik={formik}
        organizations={organizations}
        handleUploadFile={handleUploadFile}
        handleSubmit={formik.handleSubmit}
        handleCancel={handleCloseModal}
        statusChoices={statusChoices}
        policyTypes={policyTypes}
        branch={branch}
      />
      <GroupHealthInsurancePoliciesModal
        type={modalTypes.EDIT}
        open={!!selectedItem}
        title="Grup Sağlık Poliçe Güncelleme Talebi"
        subTitle="Grup Sağlık Sigortası İçin Poliçe Güncelleme"
        formik={formik}
        organizations={organizations}
        handleUploadFile={handleUploadFile}
        handleSubmit={formik.handleSubmit}
        handleCancel={handleCloseEditModal}
        statusChoices={statusChoices}
        policyTypes={policyTypes}
        branch={branch}
      />
      <AdditionalFileModal
        open={!!additionalFileID && Array.isArray(additionalFile)}
        model="grouphealthinsurancepoliciesadditionalfile"
        tempAdditionalFiles={tempAdditionalFiles}
        additionalFiles={additionalFile}
        handleUploadFile={handleUploadAdditionalFile}
        handleDeleteFile={handleDeleteFile}
        handleSubmit={handleSubmitFile}
        handleCancel={handleCloseAdditionalFile}
      />
    </Container>
  );
};

export default Policies;
